import React from 'react';
import { ComboBoxField, ComboBoxItem, type ComboBoxFieldProps } from '../base-fields/ComboBoxField';
import type { Key } from 'react-aria-components';
import { type AutocompleteOptionType, type AutocompleteTypeToModel, type FormControlOptionModel } from '@generated';
import { useAutocomplete } from '~/util/http/useAutocomplete';
import type { SieveFilterModel } from '~/util/sieve/filter';
import { usePrevious } from '~/shared/hooks/usePrevious';
import { useState } from 'react';

export interface AutocompleteSearchFieldProps<K extends AutocompleteOptionType>
	extends Omit<ComboBoxFieldProps<FormControlOptionModel>, 'value' | 'children' | 'required'> {
	searchType: K;
	onChangePassThrough?: (selected: AutocompleteTypeToModel[K] | null) => void;
	additionalFilters?: SieveFilterModel[];
	readonly?: boolean;
	required?: boolean;
	value: string | null;
	onChange: (value: string | null) => void;
}

export function AutocompleteSearchField<K extends AutocompleteOptionType>(props: AutocompleteSearchFieldProps<K>) {
	const [searchTerm, setSearchTerm] = useState('');
	const [hasSelected, setHasSelected] = useState(!!props.value);

	//if cleared by external action (like a form reset), clear the search term and selected state
	const prevValue = usePrevious(props.value);
	if (prevValue && !props.value && hasSelected) {
		setSearchTerm('');
		setHasSelected(false);
	}

	const { options, isLoading, isError, getSelectedOption, getSelectedData } = useAutocomplete<K>({
		searchType: props.searchType,
		searchTerm,
		initialValue: props.value,
		enabled: !hasSelected,
		groupResult: false,
		additionalFilters: props.additionalFilters ?? []
	});

	const displayValue = props.value && options.length > 0 ? getSelectedOption(props.value)?.label ?? searchTerm : searchTerm;
	const showOptions = !isLoading && !isError && options.length > 0 && searchTerm.length >= 3 && !hasSelected;
	const showNoOptions = !isLoading && !isError && options.length === 0 && searchTerm.length >= 3 && !hasSelected;

	const onChange = (selected: Key | null) => {
		if (!selected) {
			props.onChange(null);
			setSearchTerm('');
			setHasSelected(false);
			props.onChangePassThrough?.(null);
		} else {
			const selectedOption = getSelectedOption(selected.toString());
			props.onChange(selected.toString());
			setSearchTerm(selectedOption.label);
			setHasSelected(true);
			props.onChangePassThrough?.(getSelectedData(selected.toString()));
		}
	};

	//Only runs on initial load when there's a value but no options, which can happen when the form is loaded with an initial value but the options haven't been fetched yet
	if (props.value && options.length === 0 && isLoading) {
		return <p>Loading...</p>;
	}

	return (
		<ComboBoxField
			{...props}
			hideChevron
			selectedKey={props.value}
			onSelectionChange={onChange}
			items={showOptions ? options : []}
			inputValue={displayValue}
			onInputChange={setSearchTerm}
			allowsEmptyCollection
			noOptionsMessage={showNoOptions ? props.noOptionsMessage ?? 'No options found' : undefined}
		>
			{item => <ComboBoxItem id={item.value}>{item.label}</ComboBoxItem>}
		</ComboBoxField>
	);
}
